import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { createContext, useEffect, useMemo, useReducer, useState } from 'react';
import { z } from 'zod';
import zodToJsonSchema from 'zod-to-json-schema';

import log from '~/common/utils/log';

import type { FormValues } from '../types/FormValues';

import type { ContestManageStore } from './types/store';
import { type SectionKeyItem, getAllSectionKeys } from './utils/getAllSectionKeys';
import { getSectionValidationSchema } from './utils/getSectionValidationSchema';
import type { FormattedErrors } from './utils/formatZodErrors';
import { parseInitialResponse } from './parsers/parseInitialResponse';
import { storeReducer } from './store/store.reducer';
import type { StoreAction } from './store/types/actions';
import { createInitAction } from './store/store.actions';
import { formatValidationErrors } from './utils/formatValidationErrors';

export type TouchedValues = {
  [sectionKey: string]: {
    [fieldKey: string]: boolean;
  };
};

type ContestManageContextValue = {
  errors: FormattedErrors | null;
  store: ContestManageStore;
  dispatchStoreAction: Dispatch<StoreAction>;
  values: FormValues;
  setValues: Dispatch<SetStateAction<FormValues>>;
  sections: string[];
  isTouched: boolean;
  currentSection: SectionKeyItem | null;
  validationSchema: z.ZodObject<any, any>;
  touched: TouchedValues;
  setCurrentSection: Dispatch<SetStateAction<SectionKeyItem | null>>;
  setTouched: Dispatch<SetStateAction<TouchedValues>>;
  allSectionsKeys: SectionKeyItem[];
  setOptionalFieldsOverrides: Dispatch<SetStateAction<{ [sectionKey: string]: string[] }>>;
  allowedTagNames: string[];
};

export const ContestManageContext = createContext<ContestManageContextValue>({
  store: {
    sectionsKeys: [],
    sections: {},
    fields: {},
    fieldsCache: {},
    sectionsCache: {},
  },
  errors: null,
  dispatchStoreAction: () => {},
  values: {},
  setValues: () => {},
  sections: [],
  isTouched: false,
  currentSection: null,
  validationSchema: z.object({}),
  touched: {},
  setTouched: () => {},
  setCurrentSection: () => {},
  allSectionsKeys: [],
  setOptionalFieldsOverrides: () => {},
  allowedTagNames: [],
});

const initialValue = {
  sectionsKeys: [],
  sections: {},
  fields: {},
  fieldsCache: {},
  sectionsCache: {},
};

export function ContestManageContextProvider({
  children,
  allowedTagNames,
  sectionsData,
}: PropsWithChildren<{
  allowedTagNames?: any[];
  sectionsData?: any[];
}>) {
  const [store, dispatchStoreAction] = useReducer(storeReducer, initialValue);
  const [currentSection, setCurrentSection] = useState<SectionKeyItem | null>();
  const [values, setValues] = useState<FormValues>({});
  const [touched, setTouched] = useState<TouchedValues>({});
  const allSectionsKeys = useMemo(() => getAllSectionKeys(store), [store]);
  const [optionalFieldsOverrides, setOptionalFieldsOverrides] = useState<{
    [sectionKey: string]: string[];
  }>({});

  const validationSchema = useMemo(
    () =>
      z.object(
        allSectionsKeys.reduce((acc, item) => {
          const sectionValidationSchema = getSectionValidationSchema(
            store,
            item.path.slice(-1)[0],
            optionalFieldsOverrides[item.key]
          );
          if (sectionValidationSchema) {
            acc[item.key] = sectionValidationSchema;
          }

          return acc;
        }, {})
      ),
    [allSectionsKeys, optionalFieldsOverrides, store]
  );

  const checkIsTouched = (touchedValues: TouchedValues): boolean =>
    Object.values(touchedValues).some((section) =>
      Object.values(section).some((isTouched) => isTouched)
    );

  const errors = useMemo(() => {
    const result = validationSchema.safeParse(values);
    return formatValidationErrors(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationSchema, JSON.stringify(values)]);

  // Process server-provided sections data
  useEffect(() => {
    if (sectionsData?.length) {
      const parsedData = parseInitialResponse(sectionsData);
      dispatchStoreAction(createInitAction(parsedData));
    }
  }, [sectionsData]);

  // Set initial current section when allSectionsKeys is populated
  useEffect(() => {
    if (allSectionsKeys.length > 0 && !currentSection) {
      setCurrentSection(allSectionsKeys[0]);
    }
  }, [allSectionsKeys, currentSection]);

  log('context', {
    optionalFieldsOverrides,
    allSectionsKeys,
    errors,
    store,
    values,
    validationSchema: zodToJsonSchema(validationSchema),
  });

  const value = useMemo(
    () => ({
      store,
      dispatchStoreAction,
      currentSection,
      isTouched: checkIsTouched(touched),
      sections: store.sectionsKeys,
      setValues,
      values,
      validationSchema,
      errors,
      touched,
      setTouched,
      setCurrentSection,
      allSectionsKeys,
      setOptionalFieldsOverrides,
      allowedTagNames,
    }),
    [
      store,
      currentSection,
      values,
      validationSchema,
      errors,
      touched,
      allSectionsKeys,
      allowedTagNames,
    ]
  );

  return <ContestManageContext.Provider value={value}>{children}</ContestManageContext.Provider>;
}
